/**
 * Leviathan Web - Terminal Rack Prices Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Grid view of rack prices by terminal and product
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Calendar,
  Building2,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface RackPrice {
  id: string;
  terminalId: string;
  productId: string;
  effectiveDate: string;
  grossPrice: number | null;
  netPrice: number | null;
  totalWithTax: number | null;
  source: string | null;
  terminal: { id: string; name: string; terminalCode: string; city: string | null; state: string | null };
  product: { id: string; name: string; sku: string };
}

interface Terminal {
  id: string;
  name: string;
  terminalCode: string;
  city: string | null;
  state: string | null;
}

interface Product {
  id: string;
  name: string;
  sku: string;
}

interface NewPriceForm {
  terminalId: string;
  productId: string;
  effectiveDate: string;
  grossPrice: string;
  netPrice: string;
  source: string;
}

const TerminalRackPricesPage = () => {
  const { accessToken } = useAuth();
  const [prices, setPrices] = useState<RackPrice[]>([]);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState<NewPriceForm>({
    terminalId: '',
    productId: '',
    effectiveDate: new Date().toISOString().split('T')[0] || '',
    grossPrice: '',
    netPrice: '',
    source: 'MANUAL',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [accessToken]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [pricesRes, terminalsRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/terminals/rack-prices/all`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/terminals?isActive=true&limit=200`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/inventory?isActive=true&category=FUEL&limit=200`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (pricesRes.ok) {
        const data = await pricesRes.json();
        setPrices(Array.isArray(data) ? data : data.rackPrices || data.data || []);
      }
      if (terminalsRes.ok) {
        const data = await terminalsRes.json();
        setTerminals(Array.isArray(data) ? data : data.terminals || data.data || []);
      }
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(Array.isArray(data) ? data : data.items || data.data || []);
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPrice = async () => {
    if (!form.terminalId || !form.productId || !form.effectiveDate) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/terminals/rack-prices`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          terminalId: form.terminalId,
          productId: form.productId,
          effectiveDate: form.effectiveDate,
          grossPrice: form.grossPrice ? parseFloat(form.grossPrice) : null,
          netPrice: form.netPrice ? parseFloat(form.netPrice) : null,
          source: form.source,
        }),
      });
      if (response.ok) {
        setShowAddModal(false);
        setForm({ terminalId: '', productId: '', effectiveDate: new Date().toISOString().split('T')[0] || '', grossPrice: '', netPrice: '', source: 'MANUAL' });
        fetchData();
      }
    } catch {
      // silent
    } finally {
      setIsSaving(false);
    }
  };

  // Group prices by terminal, get latest per terminal+product
  const priceGrid: Record<string, Record<string, RackPrice>> = {};
  const allProductIds = new Set<string>();

  prices.forEach((p) => {
    allProductIds.add(p.productId);
    if (!priceGrid[p.terminalId]) priceGrid[p.terminalId] = {};
    const existing = priceGrid[p.terminalId]![p.productId];
    if (!existing || new Date(p.effectiveDate) > new Date(existing.effectiveDate)) {
      priceGrid[p.terminalId]![p.productId] = p;
    }
  });

  const gridProducts = products.filter((p) => allProductIds.has(p.id));
  const gridTerminals = terminals.filter((t) => priceGrid[t.id]);

  const formatPrice = (n: number | null) =>
    n != null ? `$${n.toFixed(4)}` : '-';
  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/pricing/rackprices" className="text-text-secondary hover:text-amber transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary uppercase tracking-wider">
              Terminal Rack Prices
            </h1>
            <p className="text-text-tertiary text-sm font-mono">Current prices by terminal and product</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="text-xs font-mono uppercase">Refresh</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Add Price</span>
          </button>
        </div>
      </div>

      {/* Price Grid */}
      {isLoading ? (
        <div className="text-center py-16 text-text-tertiary font-mono">
          <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
          <p>Loading prices...</p>
        </div>
      ) : gridTerminals.length === 0 ? (
        <div className="text-center py-16 text-text-tertiary font-mono">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No terminal rack prices found</p>
          <p className="text-xs mt-2">Add prices using the button above</p>
        </div>
      ) : (
        <div className="relative bg-panel-dark border border-panel-border overflow-x-auto">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-panel-border bg-background/50">
                <th className="text-left px-4 py-3 text-text-tertiary font-mono text-xs uppercase tracking-wider sticky left-0 bg-background/90">
                  Terminal
                </th>
                {gridProducts.map((product) => (
                  <th key={product.id} className="text-center px-4 py-3 text-amber font-mono text-xs uppercase tracking-wider min-w-[140px]">
                    {product.name}
                    <div className="text-text-tertiary font-normal">{product.sku}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gridTerminals.map((terminal, idx) => (
                <tr
                  key={terminal.id}
                  className={`border-b border-panel-border/50 hover:bg-amber/5 transition-colors ${
                    idx % 2 === 0 ? 'bg-background/30' : ''
                  }`}
                >
                  <td className="px-4 py-3 sticky left-0 bg-panel-dark">
                    <div className="text-text-primary font-semibold text-xs">{terminal.name}</div>
                    <div className="text-text-tertiary font-mono text-[10px]">
                      {terminal.terminalCode} | {[terminal.city, terminal.state].filter(Boolean).join(', ')}
                    </div>
                  </td>
                  {gridProducts.map((product) => {
                    const price = priceGrid[terminal.id]?.[product.id];
                    return (
                      <td key={product.id} className="px-4 py-3 text-center">
                        {price ? (
                          <div>
                            <div className="text-amber font-mono font-bold text-sm">
                              {formatPrice(price.netPrice ?? price.grossPrice)}
                            </div>
                            <div className="text-text-tertiary font-mono text-[10px] mt-0.5">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDate(price.effectiveDate)}
                            </div>
                            {price.source && (
                              <div className="text-text-tertiary font-mono text-[10px]">
                                {price.source}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-text-tertiary font-mono text-xs">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Price Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="relative bg-panel-dark border border-panel-border w-full max-w-md p-6 space-y-4">
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

            <h2 className="text-lg font-display font-bold text-text-primary uppercase tracking-wider">
              Add Rack Price
            </h2>

            <div>
              <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Terminal</label>
              <select
                value={form.terminalId}
                onChange={(e) => setForm({ ...form, terminalId: e.target.value })}
                className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none"
              >
                <option value="">Select Terminal...</option>
                {terminals.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.terminalCode})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Product</label>
              <select
                value={form.productId}
                onChange={(e) => setForm({ ...form, productId: e.target.value })}
                className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none"
              >
                <option value="">Select Product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Effective Date</label>
              <input type="date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Gross Price ($/gal)</label>
                <input type="number" step="0.0001" value={form.grossPrice} onChange={(e) => setForm({ ...form, grossPrice: e.target.value })} placeholder="0.0000" className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Net Price ($/gal)</label>
                <input type="number" step="0.0001" value={form.netPrice} onChange={(e) => setForm({ ...form, netPrice: e.target.value })} placeholder="0.0000" className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Source</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none">
                <option value="MANUAL">Manual Entry</option>
                <option value="OPIS">OPIS</option>
                <option value="DTN">DTN</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-text-secondary hover:text-text-primary font-mono text-sm uppercase transition-colors">Cancel</button>
              <button onClick={handleAddPrice} disabled={isSaving || !form.terminalId || !form.productId} className="px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Add Price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminalRackPricesPage;
